import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, OperatorFunction, pipe, Subject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { mapRooms, mapRoomsAdditionalParams } from 'src/app/helpers/utils/map.utils';
import { getErrorKey } from 'src/app/helpers/utils/notifications.utils';
import { isRoomsWithDifferentCurrencies } from 'src/app/helpers/utils/rooms.utils';
import { hasAllSearchValues } from 'src/app/helpers/utils/search.utils';
import { createRoomSearchParams, createUnavailableRoomSearchParams } from 'src/app/helpers/utils/http-params.utils';

import { ApplicationInsightsService } from 'src/app/services/application-insights/application-insights.service';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { NotificationsService } from 'src/app/services/notifications/notifications.service';
import { RoomStoreService } from 'src/app/services/room/room-store.service';

import { DefaultError } from 'src/app/shared/constants/errors.constants';
import { HttpStatus } from 'src/app/shared/constants/httpStatus.enum';
import { Room, RoomType } from 'src/app/shared/models/room.model';
import { RoomDetails } from 'src/app/shared/models/roomDetails.model';
import RoomSearchParameters from 'src/app/shared/models/roomSearchParams.model';
import { RoomSearchResponse } from 'src/app/shared/models/roomSearchResponse.model';

import { environment } from 'src/environments/environment';

export interface RoomsResult {
  rooms: Room[];
  type: RoomType;
}

export enum Tabs {
  ROOM_VIEW,
  MAP_VIEW
}

const apiBySearchType = {
  [RoomType.INITIAL]: 'rooms/search/all-new',
  [RoomType.AVAILABLE]: 'rooms/search/available-new'
};

@Injectable({
  providedIn: 'root'
})
export class RoomService {
  public updateSearch = new Subject<void>();
  public updateParameters = new BehaviorSubject<Partial<RoomSearchParameters>>(null);
  public isMapVisible = new BehaviorSubject<boolean>(false);
  public defaultSearch = new BehaviorSubject<boolean>(true);
  public searchParams = new BehaviorSubject<Partial<RoomSearchParameters>>(null);
  public selectedRoom = new BehaviorSubject<Room>(null);
  public highlightedRoom = new BehaviorSubject<Room>(null);
  public seeAvailabilityTrigger = new Subject<void>();

  public isRoomsWithDifferentCurrencies = false;

  public updateRoomsDetails$: Observable<RoomDetails>;
  public singleRoom$: Observable<Room>;
  public selectedRoom$: Observable<Room>;
  public roomSearchParameters$: Observable<Partial<RoomSearchParameters>>;
  public seeAvailabilityTrigger$: Observable<void>;
  public defaultSearch$: Observable<boolean>;

  public roomsStream$: Observable<RoomsResult>;

  public scrollToRoom$: Observable<Room>;
  public updateSearch$: Observable<void>;

  public activeTab: BehaviorSubject<Tabs> = new BehaviorSubject<Tabs>(Tabs.ROOM_VIEW);

  private readonly updateRoomsDetails = new Subject<RoomDetails>();
  private readonly singleRoom = new Subject<Room>();
  private scrollToRoomSubject = new BehaviorSubject<Room | null>(null);

  _proceedWithSelectedRoom = new Subject<void>();

  private currentType = null;

  constructor(
    private applicationInsightsService: ApplicationInsightsService,
    private http: HttpClient,
    private notificationsService: NotificationsService,
    private loader: LoaderService,
    private roomStore: RoomStoreService
  ) {
    this.updateRoomsDetails$ = this.updateRoomsDetails.asObservable();
    this.singleRoom$ = this.singleRoom.asObservable();
    this.selectedRoom$ = this.selectedRoom.asObservable();
    this.roomSearchParameters$ = this.searchParams.asObservable();
    this.seeAvailabilityTrigger$ = this.seeAvailabilityTrigger.asObservable();
    this.defaultSearch$ = this.defaultSearch.asObservable();
    this.scrollToRoom$ = this.scrollToRoomSubject.asObservable();
    this.updateSearch$ = this.updateSearch.asObservable();

    this.roomsStream$ = this.roomStore.rooms$;
  }

  scrollToRoom(room: Room): void {
    this.scrollToRoomSubject.next(room);
  }

  public proceedWithSelectedRoom(): void {
    this._proceedWithSelectedRoom.next();
  }

  public setSelectedRoom(room: Room): void {
    this.selectedRoom.next(room);
    sessionStorage.setItem('selectedRoom', JSON.stringify(room));
  }

  public clearSelectedRoom(): void {
    this.selectedRoom.next(null);
    sessionStorage.removeItem('selectedRoom');
  }

  public setHighlightedRoom(room: Room): void {
    this.highlightedRoom.next(room);
  }

  public getRoomDetails(centreNumber: number): void {
    this.http
      .get(`${environment.apiUrl}rooms/${centreNumber}/details`)
      .pipe(
        catchError((err) => {
          this.notificationsService.pushNotification(getErrorKey(err, 'Cannot get room details'));
          return err;
        })
      )
      .subscribe((response: RoomDetails) => {
        this.updateRoomsDetails.next(response);
      });
  }

  public setSearchState(searchState: Partial<RoomSearchParameters>): void {
    this.searchParams.next(searchState);
    sessionStorage.setItem('searchState', JSON.stringify(searchState));
  }

  public getSearchState(): Partial<RoomSearchParameters> {
    let search = this.searchParams.value;

    if (!search) {
      search = JSON.parse(sessionStorage.getItem('searchState'));
      this.searchParams.next(search);
    }
    return search;
  }

  public modifySearchState(searchState: Partial<RoomSearchParameters>): void {
    const currentState = this.searchParams.value;
    const newState = {...currentState, ...searchState};
    this.setSearchState(newState);
  }

  public getSearchMethod(params: Partial<RoomSearchParameters>): Observable<RoomsResult | RoomSearchResponse> {
    const isFullSearch = hasAllSearchValues(params);
    const hasCentre = !!params.centreNumber;

    const roomType = isFullSearch ? RoomType.AVAILABLE : RoomType.INITIAL;

    if (this.currentType !== roomType) {
      this.resetRooms();
      this.currentType = roomType;
      params.lastCentreNumber = null;
    }

    this.setIsDefaultSearch(!isFullSearch);

    if (hasCentre) {
      return this.searchOne(params).pipe(this.processSearchResult(roomType));
    }

    return this.searchAll(roomType, params as RoomSearchParameters);
  }

  public searchOne(parameters: Partial<RoomSearchParameters>): Observable<RoomsResult> {
    return this.http
      .get<RoomSearchResponse>(`${environment.apiUrl}rooms/${parameters.centreNumber}`, {
        params: createRoomSearchParams(parameters)
      })
      .pipe(
        map((room: Room | any): RoomsResult => {
          if (room.message) {
            return { rooms: [], type: null };
          }

          this.singleRoom.next(room);
          return { rooms: [room], type: room.price ? RoomType.AVAILABLE : RoomType.INITIAL };
        }),
        this.handleRoomSearchError()
      );
  }

  private search(type: RoomType, incomingParameters: Partial<RoomSearchParameters>): Observable<RoomsResult> {
    const lastFoundRoom: Room = this.roomStore.getLastFoundRoom();
    if (lastFoundRoom) {
      incomingParameters.lastCentreNumber = lastFoundRoom.centreNumber;
    }

    const params =
      type === RoomType.AVAILABLE
        ? createRoomSearchParams(incomingParameters as RoomSearchParameters)
        : createUnavailableRoomSearchParams(incomingParameters as RoomSearchParameters);

    return this.http.get(`${environment.apiUrl}${apiBySearchType[type]}`, { params }).pipe(
      map((rooms: RoomSearchResponse): RoomsResult => ({ rooms, type })),
      this.handleRoomSearchError()
    );
  }

  private resetRooms(): void {
    this.roomStore.reset();
    const search = this.getSearchState();
    this.setSearchState({ ...search, lastCentreNumber: null });
  }

  private setIsDefaultSearch(value: boolean): void {
    this.defaultSearch.next(value);
  }

  private searchAll(roomsType: RoomType, incomingParameters: RoomSearchParameters): Observable<RoomsResult> {
    this.loader.start();
    const parameters = Object.assign({}, incomingParameters);
    this.clearSelectedRoom();

    // track error if parameters hasn't location and coordinates
    if (!parameters.location && !parameters.coordinatesLongitude && !parameters.coordinatesLatitude) {
      this.applicationInsightsService.logError(new Error(JSON.stringify(parameters)));
    }

    const source$: Observable<RoomsResult> = this.search(roomsType, parameters);
    return source$.pipe(this.processSearchResult(roomsType));
  }

  private processSearchResult(type: RoomType): OperatorFunction<RoomsResult, RoomsResult> {
    return pipe(
      tap((response: RoomsResult) => {
        const rooms = mapRoomsAdditionalParams(mapRooms(response.rooms));

        this.isRoomsWithDifferentCurrencies = isRoomsWithDifferentCurrencies(rooms);

        this.roomStore.addRooms({ rooms, type });
        this.loader.stop();
      }),

      catchError((): Observable<RoomsResult> => {
        this.roomStore.reset();
        this.loader.stop();
        return of({ rooms: [], type: null });
      })
    );
  }

  private handleRoomSearchError = (): OperatorFunction<RoomsResult, RoomsResult> =>
    catchError((err): Observable<RoomsResult> => {
      if (err && err.status === HttpStatus.CODE500) {
        this.notificationsService.pushNotification(getErrorKey(err, DefaultError));
      }
      return of({ rooms: [], type: null });
    });
}
